"use client";

import React, { useState, useEffect } from "react";
import Link from "next/link";
import { useRouter, usePathname } from "next/navigation";
import { useSelector, useDispatch } from "react-redux";

import { logout as logoutAction } from "../../../redux/slices/userSlice";
import { logout, getUserFromCookies } from "../../../utils/cookieAuth";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../ui/card";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "../../ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import { Skeleton } from "../../ui/skeleton";
import ConfirmDialog from "../../ui/confirm-dialog";
import { cn } from "../../../lib/utils";
import {
  MdHome,
  MdArticle,
  MdEvent,
  MdPhotoLibrary,
  MdVideoLibrary,
  MdMenu,
  MdClose,
  MdSearch,
  MdNotifications,

  MdSettings,
  MdLogout,
  MdExpandMore,
  MdNewspaper,
  MdCampaign,
  MdTv,
  MdInsertDriveFile,
  MdViewCarousel,
  MdGroup,
  MdBugReport,
  MdDashboard,
  MdMessage,
  MdBarChart,
  MdShare,
  MdPeople,
  MdDirectionsCar,
  MdAdminPanelSettings,
  MdHowToVote,
} from "react-icons/md";
import { usePermissions } from "../../../hooks/usePermissions";
import NotificationBell from "../notifications/NotificationBell";



// User Navigation Component (Avatar + Dropdown)
const UserNav = () => {
  const router = useRouter();
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.user.user);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Show logout confirmation
  const handleLogoutClick = () => {
    setShowLogoutConfirm(true);
  };

  // Logout function with confirmation
  const handleLogoutConfirm = async () => {
    try {
      setIsLoggingOut(true);

      // Call logout function and clear Redux
      await logout();
      dispatch(logoutAction());

      setShowLogoutConfirm(false);

      // Force a complete page reload to ensure all cached data is cleared
      // Use replace to prevent back button issues
      window.location.replace("/dashboard/login");
    } catch (error) {
      setIsLoggingOut(false);
      setShowLogoutConfirm(false);

      // Even if logout fails, clear Redux and redirect to login
      dispatch(logoutAction());
      window.location.replace("/dashboard/login");
    }
  };

  // Cancel logout
  const handleLogoutCancel = () => {
    setShowLogoutConfirm(false);
  };

  // Profile settings function
  const handleProfileSettings = () => {
    router.push("/dashboard/profile-setting");
  };

  if (!mounted || !currentUser) {
    return (
      <div className="flex items-center gap-2 sm:gap-4">
        <Skeleton className="h-8 w-8 sm:h-9 sm:w-9 rounded-full" />
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center gap-2 sm:gap-4">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="relative h-8 w-8 sm:h-9 sm:w-9 rounded-full">
              <Avatar className="h-8 w-8 sm:h-9 sm:w-9">
                {currentUser?.avatar && <AvatarImage src={currentUser.avatar} />}
                <AvatarFallback className="bg-primary text-primary-foreground font-semibold text-xs sm:text-sm">
                  {(() => {
                    const firstInitial = currentUser?.firstName?.charAt(0)?.toUpperCase() || "";
                    const lastInitial = currentUser?.lastName?.charAt(0)?.toUpperCase() || "";
                    const initials = firstInitial + lastInitial;

                    if (initials) return initials;
                    if (currentUser?.username) return currentUser.username.charAt(0).toUpperCase();
                    if (currentUser?.email) return currentUser.email.charAt(0).toUpperCase();
                    return "U";
                  })()}
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56 sm:w-60 md:w-64">
            <DropdownMenuLabel>
              <div className="flex flex-col space-y-0">
                <p className="text-sm font-semibold truncate">
                  {currentUser?.firstName
                    ? `${currentUser.firstName}${currentUser.lastName ? ' ' + currentUser.lastName : ''}`
                    : currentUser?.username || "User"}
                </p>
                <p className="text-xs text-muted-foreground truncate">
                  {currentUser?.username ? `@${currentUser.username}` : currentUser?.email || ""}
                </p>
                <Badge variant="secondary" className="text-[10px] h-5 px-2 w-fit uppercase font-medium mt-1">
                  {currentUser?.role || "User"}
                </Badge>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={handleProfileSettings}
              className="cursor-pointer"
            >
              <MdSettings className="mr-2 h-4 w-4" />
              <span>Profile Settings</span>
            </DropdownMenuItem>

            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={handleLogoutClick}
              className="cursor-pointer text-destructive"
            >
              <MdLogout className="mr-2 h-4 w-4" />
              <span>Logout</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Logout Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showLogoutConfirm}
        onClose={handleLogoutCancel}
        onConfirm={handleLogoutConfirm}
        title="Logout Confirmation"
        description="Are you sure you want to logout? You will need to login again to access the dashboard."
        confirmText="Logout"
        cancelText="Cancel"
        isLoading={isLoggingOut}
        variant="warning"
      />
    </>
  );
};

// Sidebar Component with Fixed Height and Internal Scrolling
const Sidebar = ({ isOpen, onClose }) => {
  const router = useRouter();
  const pathname = usePathname();
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.user.user);
  const { hasPermission } = usePermissions();
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const [isClient, setIsClient] = useState(false);

  // Prevent hydration mismatch by rendering navigation only on client
  useEffect(() => {
    setIsClient(true);
  }, []);

  const navigation = [
    { name: "Dashboard", icon: MdHome, href: "/dashboard", permission: "dashboard" },
    { name: "Blog Posts", icon: MdArticle, href: "/dashboard/content/blog", permission: "posts" },
    { name: "Pages", icon: MdInsertDriveFile, href: "/dashboard/content/page", permission: "pages" },
    {
      name: "Media Coverage",
      icon: MdTv,
      href: "/dashboard/content/media-coverage",
      permission: "media_coverage"
    },
    {
      name: "Press Releases",
      icon: MdNewspaper,
      href: "/dashboard/content/press-release",
      permission: "press_releases"
    },
    { name: "Events", icon: MdEvent, href: "/dashboard/content/events", permission: "events" },
    { name: "Galleries", icon: MdPhotoLibrary, href: "/dashboard/content/gallery", permission: "galleries" },
    { name: "Videos", icon: MdVideoLibrary, href: "/dashboard/content/video", permission: "videos" },
    { name: "Issues Management", icon: MdBugReport, href: "/dashboard/issues", permission: "issues" },
    { name: "Polls Management", icon: MdBarChart, href: "/dashboard/polls", permission: "polls" },
    { name: "User Management", icon: MdPeople, href: "/dashboard/users", permission: "users" },
    { name: "Staff Management", icon: MdAdminPanelSettings, href: "/dashboard/staff", permission: "staff" },
    { name: "Volunteer Submissions", icon: MdGroup, href: "/dashboard/volunteer-submissions", permission: "volunteers" },
    {
      name: "Vehicle Management",
      icon: MdDirectionsCar,
      href: "/dashboard/vehicle-management",
      permission: "vehicles",
      submenu: [
        { name: "Political Events", href: "/dashboard/vehicle-management/political-events" },
        { name: "Vehicles Submissions", href: "/dashboard/vehicle-management/vehicles-submissions" },
      ]
    },
    { name: "Groups & Messaging", icon: MdMessage, href: "/dashboard/groups", permission: "groups" },
    { name: "Notifications", icon: MdNotifications, href: "/dashboard/notifications" },
    {
      name: "Social Media Management",
      icon: MdShare,
      href: "/dashboard/social-media",
      permission: "social_media",
      submenu: [
        { name: "Social Accounts", href: "/dashboard/social-media/accounts" },
        { name: "Social Media Posts", href: "/dashboard/social-media/posts" },
      ]
    },
    {
      name: "Meta Ads Manager",
      icon: MdCampaign,
      href: "/dashboard/meta-ads",
      permission: "social_media",
      submenu: [
        { name: "Campaigns", href: "/dashboard/meta-ads/campaigns" },
        { name: "Ads Account", href: "/dashboard/meta-ads/ads-account" },
      ]
    },
    {
      name: "Website Settings",
      icon: MdSettings,
      href: "/dashboard/settings",
      permission: "settings",
      submenu: [
        { name: "Languages", href: "/dashboard/settings/languages" },
        { name: "AI Models", href: "/dashboard/settings/ai-models" },
        { name: "Header Settings", href: "/dashboard/settings/header" },
        { name: "Footer Settings", href: "/dashboard/settings/footer" },
        { name: "Social Media", href: "/dashboard/settings/social-media" },
        { name: "Homepage Settings", href: "/dashboard/settings/homepage" },
        { name: "SEO Settings", href: "/dashboard/settings/seo" },
        { name: "Theme Colors", href: "/dashboard/settings/theme" },
        { name: "Notification Settings", href: "/dashboard/settings/notification-settings" },
        { name: "PWA Settings", href: "/dashboard/settings/pwa-settings" },
      ]
    },
    {
      name: "AI Election Reports",
      icon: MdHowToVote,
      href: "/dashboard/election-reports/lok-sabha",
      permission: "election_reports",
      submenu: [
        { name: "Lok Sabha (PC) Results", href: "/dashboard/election-reports/lok-sabha" },
        { name: "Vidhan Sabha (AC) Results", href: "/dashboard/election-reports/vidhan-sabha" },
      ]
    },
  ];

  const handleNavigation = (href) => {
    router.push(href);
    // Close mobile sidebar when clicking nav item
    if (onClose && window.innerWidth < 1024) {
      onClose();
    }
  };



  return (
    <>
      {/* Mobile backdrop */}
      <div
        className={cn(
          "fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden transition-opacity",
          isOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        )}
        onClick={onClose}
      />

      {/* Sidebar */}
      <div
        className={cn(
          // Base layout - hidden on mobile, visible on lg+
          "lg:flex lg:flex-col lg:w-64",
          // Mobile overlay with smooth transitions
          "fixed inset-y-0 left-0 z-50 flex flex-col w-64 lg:static lg:z-auto",
          "transition-transform duration-300 ease-in-out lg:transition-none lg:transform-none",
          isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
          // Background and styling
          "bg-card border-r border-border",
          "shadow-xl lg:shadow-none",
          "h-full"
        )}
      >
        {/* Sidebar Content Container with flex layout */}
        <div className="flex flex-col h-full">
          {/* Header - Fixed at top */}
          <div className="flex-shrink-0 flex items-center justify-between h-12 sm:h-14 md:h-16 px-3 sm:px-4 md:px-6 border-b border-border bg-card">
            <div className="flex items-center space-x-2 sm:space-x-2.5 md:space-x-3">
              <div className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 rounded-md sm:rounded-lg bg-primary flex items-center justify-center">
                <MdDashboard className="h-3.5 w-3.5 sm:h-4 sm:w-4 md:h-5 md:w-5 text-primary-foreground" />
              </div>
              <h1 className="text-sm sm:text-base md:text-lg font-bold text-foreground">Dashboard</h1>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="lg:hidden h-7 w-7 sm:h-8 sm:w-8 p-0"
              onClick={onClose}
            >
              <MdClose className="h-4 w-4 sm:h-5 sm:w-5" />
            </Button>
          </div>

          {/* Navigation - Scrollable area */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden">
            <nav className="p-2 sm:p-3 md:p-4 space-y-1 sm:space-y-1.5 md:space-y-2">
              {!isClient || !currentUser ? (
                // Loading Skeleton for Navigation (shown during SSR and loading)
                <>
                  {[...Array(8)].map((_, index) => (
                    <div key={index} className="flex items-center px-2 py-1.5 sm:px-2.5 sm:py-2 md:px-3 md:py-2 space-x-2 sm:space-x-2.5 md:space-x-3">
                      <Skeleton className="h-3.5 w-3.5 sm:h-4 sm:w-4 rounded" />
                      <Skeleton className="h-3 sm:h-3.5 md:h-4 flex-1" />
                    </div>
                  ))}
                </>
              ) : (
                navigation
                  .filter((item) => !item.permission || hasPermission(item.permission))
                  .map((item, index) => {
                    // Special handling for Dashboard route to avoid matching all /dashboard/* routes
                    const isActive = item.href === "/dashboard"
                      ? pathname === "/dashboard"
                      : pathname === item.href || pathname.startsWith(item.href + "/");

                    // Handle items with submenu
                    if (item.submenu && item.submenu.length > 0) {
                      const hasActiveSubmenu = item.submenu.some(
                        (subitem) =>
                          pathname === subitem.href ||
                          pathname.startsWith(subitem.href + "/")
                      );
                      const isSubmenuOpen = openSubmenu === item.name || hasActiveSubmenu;

                      return (
                        <div key={item.name} className="space-y-1">
                          <button
                            onClick={() => {
                              if (openSubmenu === item.name) {
                                setOpenSubmenu(null);
                              } else {
                                setOpenSubmenu(item.name);
                              }
                            }}
                            className={cn(
                              "flex items-center px-2 py-1.5 sm:px-2.5 sm:py-2 md:px-3 md:py-2 text-xs sm:text-sm font-medium rounded-md w-full text-left",
                              "transition-colors",
                              hasActiveSubmenu
                                ? "bg-primary text-primary-foreground"
                                : "text-muted-foreground hover:text-foreground hover:bg-muted"
                            )}
                          >
                            <div className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0">
                              <item.icon className="h-4 w-4 sm:h-5 sm:w-5" />
                            </div>
                            <span className="ml-2 sm:ml-2.5 md:ml-3 truncate flex-1">
                              {item.name}
                            </span>
                            <MdExpandMore
                              className={cn(
                                "h-3.5 w-3.5 sm:h-4 sm:w-4 transition-transform flex-shrink-0",
                                isSubmenuOpen ? "rotate-180" : "rotate-0"
                              )}
                            />
                          </button>

                          {/* Submenu items */}
                          {isSubmenuOpen && (
                            <div className="ml-4 sm:ml-5 md:ml-6 space-y-0.5 sm:space-y-1">
                              {item.submenu.map((subitem, subIndex) => {
                                const isSubmenuActive =
                                  pathname === subitem.href ||
                                  pathname.startsWith(subitem.href + "/");
                                return (
                                  <button
                                    key={subitem.name}
                                    onClick={() => handleNavigation(subitem.href)}
                                    className={cn(
                                      "flex items-center px-2 py-1.5 sm:px-2.5 sm:py-2 md:px-3 md:py-2 text-xs sm:text-sm rounded-md w-full text-left",
                                      "transition-colors",
                                      isSubmenuActive
                                        ? "bg-primary/20 text-primary"
                                        : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                                    )}
                                  >
                                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-current opacity-60 mr-2 sm:mr-2.5 md:mr-3 flex-shrink-0" />
                                    <span className="truncate">
                                      {subitem.name}
                                    </span>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    }

                    // Handle items with children (legacy support)
                    if (item.children && item.children.length > 0) {
                      const hasActiveChild = item.children.some(
                        (child) =>
                          pathname === child.href ||
                          pathname.startsWith(child.href + "/")
                      );

                      return (
                        <div key={item.name} className="space-y-1">
                          <div
                            className={cn(
                              "flex items-center px-2 py-1.5 sm:px-2.5 sm:py-2 md:px-3 md:py-2 text-xs sm:text-sm font-medium rounded-md w-full text-left",
                              "transition-colors",
                              hasActiveChild
                                ? "bg-primary text-primary-foreground"
                                : "text-muted-foreground hover:text-foreground hover:bg-muted"
                            )}
                          >
                            <div className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0">
                              <item.icon className="h-4 w-4 sm:h-5 sm:w-5" />
                            </div>
                            <span className="ml-2 sm:ml-2.5 md:ml-3 truncate">
                              {item.name}
                            </span>
                            {hasActiveChild && (
                              <div className="ml-auto h-1.5 w-1.5 sm:h-2 sm:w-2 rounded-full bg-current opacity-80 flex-shrink-0" />
                            )}
                          </div>

                          {/* Children items */}
                          <div className="ml-4 sm:ml-5 md:ml-6 space-y-0.5 sm:space-y-1">
                            {item.children.map((child, childIndex) => {
                              const isChildActive =
                                pathname === child.href ||
                                pathname.startsWith(child.href + "/");
                              return (
                                <button
                                  key={child.name}
                                  onClick={() => handleNavigation(child.href)}
                                  className={cn(
                                    "flex items-center px-2 py-1.5 sm:px-2.5 sm:py-2 md:px-3 md:py-2 text-xs sm:text-sm rounded-md w-full text-left",
                                    "transition-colors",
                                    isChildActive
                                      ? "bg-primary/20 text-primary"
                                      : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                                  )}
                                >
                                  <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-current opacity-60 mr-2 sm:mr-2.5 md:mr-3 flex-shrink-0" />
                                  <span className="truncate">
                                    {child.name}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    }

                    // Handle regular items without children
                    return (
                      <button
                        key={item.name}
                        onClick={() => handleNavigation(item.href)}
                        className={cn(
                          "flex items-center px-2 py-1.5 sm:px-2.5 sm:py-2 md:px-3 md:py-2 text-xs sm:text-sm font-medium rounded-md w-full text-left",
                          "transition-colors",
                          isActive
                            ? "bg-primary text-primary-foreground"
                            : "text-muted-foreground hover:text-foreground hover:bg-muted"
                        )}
                      >
                        <div className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0">
                          <item.icon className="h-4 w-4 sm:h-5 sm:w-5" />
                        </div>
                        <span className="ml-2 sm:ml-2.5 md:ml-3 truncate">
                          {item.name}
                        </span>
                      </button>
                    );
                  })
              )}
            </nav>
          </div>

          {/* Copyright Footer - Fixed at bottom */}
          <div className="flex-shrink-0 p-3 sm:p-4 border-t border-border bg-muted/30">
            <p className="text-xs text-center text-muted-foreground">
              &copy; {new Date().getFullYear()} Lok Election
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

// Page Header Component - Reusable with Back Button
const PageHeader = ({ title, subtitle, showBackButton = true, children }) => {
  const router = useRouter();

  const handleBack = () => {
    router.back();
  };

  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 mb-1 sm:mb-2">
      <div className="flex items-center gap-2 sm:gap-2.5 md:gap-3">
        {showBackButton && (
          <Button
            variant="outline"
            size="icon"
            onClick={handleBack}
            className="h-7 w-7 sm:h-8 sm:w-8 md:h-9 md:w-9 shrink-0 text-foreground border-border hover:bg-muted/50"
          >
            <MdExpandMore className="h-4 w-4 sm:h-4.5 sm:w-4.5 md:h-5 md:w-5 rotate-90" />
          </Button>
        )}
        <div className="space-y-0.2 min-w-0">
          <h2 className="text-md  font-bold text-foreground truncate m-0">{title}</h2>
          {subtitle && (
            <div className="text-sm text-muted-foreground truncate m-0">{subtitle}</div>
          )}
        </div>
      </div>
      {children && <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">{children}</div>}
    </div>
  );
};

// Header Component - Responsive and Fixed with Page Title
const DashboardHeader = ({ onMenuClick }) => {
  const pathname = usePathname();
  const currentUser = useSelector((state) => state.user.user);

  // Map of routes to page titles
  const getPageTitle = (path) => {
    const titleMap = {
      '/dashboard': 'Dashboard',
      '/dashboard/content/blog': 'Blog Posts',
      '/dashboard/content/page': 'Pages',
      '/dashboard/content/media-coverage': 'Media Coverage',
      '/dashboard/content/press-release': 'Press Releases',
      '/dashboard/content/events': 'Events',
      '/dashboard/content/gallery': 'Galleries',
      '/dashboard/content/video': 'Videos',
      '/dashboard/content/editor': 'Content Editor',
      '/dashboard/issues': 'Issues Management',
      '/dashboard/users': 'User Management',
      '/dashboard/staff': 'Staff Management',
      '/dashboard/volunteer-submissions': 'Volunteer Submissions',
      '/dashboard/vehicle-management': 'Vehicle Management',
      '/dashboard/vehicle-management/political-events': 'Political Events',
      '/dashboard/vehicle-management/vehicles-submissions': 'Vehicles Submissions',
      '/dashboard/groups': 'Groups & Messaging',
      '/dashboard/groups/create': 'Create Group',
      '/dashboard/notifications': 'Notifications',
      '/dashboard/settings': 'Website Settings',
      '/dashboard/settings/header': 'Header Settings',
      '/dashboard/settings/footer': 'Footer Settings',
      '/dashboard/settings/social-media': 'Social Media',
      '/dashboard/settings/homepage': 'Homepage Settings',
      '/dashboard/settings/seo': 'SEO Settings',
      '/dashboard/settings/theme': 'Theme Colors',
      '/dashboard/settings/notification-settings': 'Notification Settings',
      '/dashboard/settings/pwa-settings': 'PWA Settings',
      '/dashboard/menus': 'Menu Management',
      '/dashboard/election-reports': 'Election Reports - Dashboard',
      '/dashboard/election-reports/browse-data': 'Browse Election Data',
      '/dashboard/election-reports/lok-sabha': 'Lok Sabha Results',
      '/dashboard/election-reports/vidhan-sabha': 'Vidhan Sabha Results',
      '/dashboard/election-reports/party-performance': 'Party Performance',
      '/dashboard/election-reports/advanced-search': 'Advanced Search',
      '/dashboard/election-reports/summary': 'Summary Reports',
    };

    // Check for dynamic routes
    if (path.match(/\/dashboard\/vehicle-management\/vehicles-submissions\/edit\/[^\/]+$/)) {
      return 'Edit Vehicle Submission';
    }
    if (path.match(/\/dashboard\/vehicle-management\/vehicles-submissions\/create$/)) {
      return 'Create Vehicle Submission';
    }
    if (path.match(/\/dashboard\/groups\/[^\/]+$/)) {
      return 'Group Details';
    }
    if (path.match(/\/dashboard\/groups\/[^\/]+\/settings$/)) {
      return 'Group Settings';
    }
    if (path.match(/\/dashboard\/issues\/[^\/]+$/)) {
      return 'Issue Details';
    }
    if (path.match(/\/dashboard\/staff\/[^\/]+\/edit$/)) {
      return 'Edit Staff';
    }
    if (path.match(/\/dashboard\/staff\/[^\/]+$/)) {
      return 'Staff Details';
    }

    return titleMap[path] || 'Dashboard';
  };

  const pageTitle = getPageTitle(pathname);

  // Update document title
  useEffect(() => {
    document.title = `${pageTitle} - Dashbaord`;
  }, [pageTitle]);

  return (
    <header className="z-30 h-12 sm:h-14 md:h-16 bg-card/95 backdrop-blur-sm border-b border-border">
      <div className="flex items-center justify-between h-full px-3 sm:px-4 lg:px-6">
        {/* Left side - Mobile menu button */}
        <div className="flex items-center gap-2 sm:gap-3">
          <Button
            variant="ghost"
            size="sm"
            className="lg:hidden h-8 w-8 sm:h-9 sm:w-9 md:h-10 md:w-10 p-0"
            onClick={onMenuClick}
          >
            <MdMenu className="h-4 w-4 sm:h-5 sm:w-5 text-foreground" />
          </Button>
        </div>

        {/* Spacer */}
        <div className="flex-1"></div>

        {/* Notification Bell + User Nav */}
        <div className="flex items-center gap-1 sm:gap-2">
          <NotificationBell />
          <UserNav />
        </div>
      </div>
    </header>
  );
};

// Stats Grid Component
const StatsGrid = ({ stats }) => {
  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      {stats.map((stat, index) => (
        <Card key={index}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {stat.title}
            </CardTitle>
            <stat.icon className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">
              {stat.value}
            </div>
            <div className="flex items-center space-x-2 text-xs">
              <Badge
                variant={
                  stat.changeType === "positive"
                    ? "success"
                    : stat.changeType === "negative"
                      ? "destructive"
                      : "secondary"
                }
                className="px-1"
              >
                {stat.change}
              </Badge>
              <span className="text-muted-foreground">{stat.period}</span>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// Quick Actions Component
const QuickActions = ({ actions }) => {
  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {actions.map((action, index) => (
        <Card
          key={index}
          className="cursor-pointer group"
        >
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <action.icon className="h-5 w-5 text-primary" />
              <span>{action.title}</span>
            </CardTitle>
            <CardDescription>{action.description}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <Button size="sm" className="flex-1">
                {action.primaryAction}
              </Button>
              <Button variant="outline" size="sm" className="flex-1">
                {action.secondaryAction}
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// Recent Activity Component
const RecentActivity = ({ activities }) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Recent Activity</CardTitle>
        <CardDescription>Latest updates and changes</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {activities.map((activity, index) => (
          <div key={index} className="flex items-start space-x-3">
            <div
              className={cn(
                "w-2 h-2 rounded-full mt-2",
                activity.type === "create"
                  ? "bg-green-500"
                  : activity.type === "update"
                    ? "bg-primary"
                    : activity.type === "delete"
                      ? "bg-destructive"
                      : "bg-muted-foreground"
              )}
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground">
                {activity.title}
              </p>
              <p className="text-xs text-muted-foreground">
                {activity.description}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {activity.time}
              </p>
            </div>
            <Badge variant="outline" className="text-xs">
              {activity.type}
            </Badge>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export {
  Sidebar,
  DashboardHeader,
  PageHeader,
  StatsGrid,
  QuickActions,
  RecentActivity,
};